import React, {
  memo, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { IntentResolver } from './IntentResolver';
import { IntentResolutionProgress } from './IntentResolutionProgress';
import { useQuantityEstimator } from '../../hooks/useQuantityEstimator';
import { PHASES } from '../../constants';

const IntentResolverHoisted = ({ children }) => {
  return children;
};
IntentResolverHoisted.displayName = 'IntentResolverHoisted';

IntentResolverHoisted.dataModel = extend(IntentResolver, useQuantityEstimator);

const isEqual = (prevProps, nextProps) => {
  return (
    prevProps.intents.join(',') === nextProps.intents.join(',')
    && prevProps.showLoader === nextProps.showLoader
    && prevProps.batchSize === nextProps.batchSize
    && prevProps.showLoaderOnComplete === nextProps.showLoaderOnComplete
    && prevProps.itemCount === nextProps.itemCount
    && prevProps.enableQuantityEstimation === nextProps.enableQuantityEstimation
  );
};

export const QuickIntentResolve = memo(({
  intents,
  itemCount,
  onAllResolved,
  onEachResolved,
  showLoader,
  batchSize = 10,
  updateProgress,
  showLoaderOnComplete = false,
  enableQuantityEstimation = false,
  intentGroups,
  listTitle,
  aiPromptMessage,
  estimationMetaData,
}) => {
  const { isLocalized } = useStore();
  const { deliveryZip: serverDeliveryZip } = useContext(ExperienceContext);
  const THDCustomer = useThdCustomer();
  const [wait, setWait] = useState(true);
  const [intentItemIdMap, setIntentItemIdMap] = useState({});
  const { svocID = '' } = THDCustomer || {};

  const [currentPhase, setCurrentPhase] = useState(PHASES.RESOLVING);
  const isEstimating = currentPhase === PHASES.ESTIMATING;
  const resolutionCompleteRef = useRef(false);

  const intentsToResolve = useMemo(() => {
    return (intents || []).filter((intent) => intent && intent?.trim()?.length >= 1);
  }, [intents]);

  const additionalSearchParams = useMemo(() => {
    const additionalSearchData = {
      deliveryZip: serverDeliveryZip,
      svocID,
      sponsored: false
    };
    if (!isLocalized) {
      return additionalSearchData;
    }
    if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails) {
      const deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
      additionalSearchData.deliveryZip = deliveryZipCode?.zipcode;
    }
    return additionalSearchData;
  }, [serverDeliveryZip, isLocalized, svocID]);

  useEffect(() => {
    if (additionalSearchParams.deliveryZip && wait) {
      setWait(false);
    } else if (!additionalSearchParams.deliveryZip && wait) {
      setTimeout(() => setWait(false), 2000);
    }
  }, [additionalSearchParams]);

  const progress = useMemo(() => {
    const intentsCount = intentsToResolve.length;
    const completedCount = Object.keys(intentItemIdMap).length;
    const percentage = intentsCount > 0 ? Math.round((completedCount / intentsCount) * 100) : 100;
    return {
      intentsCount,
      itemCount: itemCount || intentsCount,
      completedCount,
      resolvedCount: Object.values(intentItemIdMap).filter((items) => items?.length > 0).length,
      isResolving: completedCount < intentsCount,
      resolutionProgress: percentage
    };
  }, [intentItemIdMap, intentsToResolve]);

  useEffect(() => {
    updateProgress(progress);
    if (progress.resolutionProgress === 100 && progress.intentsCount > 0) {
      if (!resolutionCompleteRef.current) {
        resolutionCompleteRef.current = true;
        if (!enableQuantityEstimation) {
          onAllResolved(intentItemIdMap);
        } else {
          setCurrentPhase(PHASES.ESTIMATING);
        }
      }
    }
  }, [progress, updateProgress, onAllResolved, intentItemIdMap, enableQuantityEstimation]);

  // Phase 2: Quantity Estimation
  const {
    estimatedMaterials,
    estimationProgress,
    estimationError,
  } = useQuantityEstimator({
    intentGroups,
    resolvedIntentItemMap: intentItemIdMap,
    listTitle,
    aiPromptMessage,
    metaData: estimationMetaData,
    enabled: enableQuantityEstimation && isEstimating
  });

  // When estimation completes, fire onAllResolved with both arguments
  useEffect(() => {
    if (!enableQuantityEstimation || !isEstimating) {
      return;
    }
    if (!estimationProgress.isDone) {
      return;
    }

    setCurrentPhase(PHASES.COMPLETE);
    onAllResolved(intentItemIdMap, estimatedMaterials, !!estimationError);
  }, [enableQuantityEstimation, currentPhase, estimationProgress.isDone,
    onAllResolved, intentItemIdMap, estimatedMaterials, estimationError]);

  const activeIntents = useMemo(() => {
    const completedCount = Object.keys(intentItemIdMap).length;
    const endIndex = Math.min(completedCount + batchSize, intentsToResolve.length);
    return intentsToResolve.slice(0, endIndex);
  }, [intentsToResolve, intentItemIdMap, batchSize]);

  /**
   * Handles the resolution of a single intent by storing the resolved product data
   * and notifying the parent component. Prevents duplicate resolutions for the same intent.
   *
   * @param {string} intent - The intent string to resolve (e.g., 'Landscape Fabric')
   * @param {Object} product - The resolved product data
   * @param {string[]} product.itemIds - Array of item IDs associated with the product
   * @param {string} product.sku - The SKU of the product
   * @param {string} product.omsId - The OMS ID of the product
   * @param {number} product.price - The price of the product
   * @returns {void}
   */
  const onResolved = useCallback((intent, product) => {
    // Prevent duplicate resolutions using functional update
    setIntentItemIdMap((prevMap) => {
      if (Object.hasOwn(prevMap, intent)) {
        return prevMap;
      }
      return { ...prevMap, [intent]: product };
    });

    onEachResolved(intent, product?.itemIds ?? null);
  }, [onEachResolved]);

  const showProgressBar = showLoader && (
    progress.isResolving || wait || showLoaderOnComplete || isEstimating
  );

  const totalPhases = enableQuantityEstimation ? 2 : 1;

  const displayProgress = useMemo(() => {
    if (isEstimating) {
      return { ...progress, resolutionProgress: estimationProgress.overallProgress };
    }
    return progress;
  }, [currentPhase, progress, estimationProgress]);

  return (
    <QueryProvider cacheKey="quick-intent-resolve">
      <IntentResolverHoisted>
        {
          (activeIntents || []).map((intentItem, _index) => {
            // Skip already resolved intents
            if (Object.hasOwn(intentItemIdMap, intentItem) || wait) {
              return null;
            }
            return (
              <IntentResolver
                key={`quick-intent-resolve-${intentItem}`.replace(/\s+/g, '-')}
                intent={intentItem}
                onResolved={onResolved}
                additionalSearchParams={additionalSearchParams}
              />
            );
          })
        }
      </IntentResolverHoisted>
      {
        showProgressBar && (
          <IntentResolutionProgress
            progress={displayProgress}
            phase={currentPhase}
            totalPhases={totalPhases}
          />
        )
      }
    </QueryProvider>
  );
}, isEqual);

QuickIntentResolve.displayName = 'QuickIntentResolve';

QuickIntentResolve.propTypes = {
  intents: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateProgress: PropTypes.func,
  onAllResolved: PropTypes.func,
  onEachResolved: PropTypes.func,
  showLoader: PropTypes.bool,
  showLoaderOnComplete: PropTypes.bool,
  batchSize: PropTypes.number,
  itemCount: PropTypes.number,
  enableQuantityEstimation: PropTypes.bool,
  intentGroups: PropTypes.arrayOf(PropTypes.shape({
    groupName: PropTypes.string,
    materials: PropTypes.arrayOf(PropTypes.shape({})),
  })),
  listTitle: PropTypes.string,
  aiPromptMessage: PropTypes.string,
  estimationMetaData: PropTypes.shape({
    refType: PropTypes.string,
    refId: PropTypes.string,
    channel: PropTypes.string,
  }),
};

QuickIntentResolve.defaultProps = {
  onEachResolved: () => {},
  onAllResolved: () => {},
  updateProgress: () => {},
  showLoader: false,
  showLoaderOnComplete: false,
  batchSize: 10,
  itemCount: 0,
  enableQuantityEstimation: false,
  intentGroups: undefined,
  listTitle: undefined,
  aiPromptMessage: undefined,
  estimationMetaData: undefined,
};

QuickIntentResolve.dataModel = extend(IntentResolverHoisted);
